import { extractMediaUrl, getStrapiFields } from './helpers'

/** Figma collage rectangle keys */
export type AboutCollageSlot = '53' | '56' | '57' | '59' | '60'

/**
 * Layout order for the About collage (matches DESKTOP_ABOUT_IMAGES in About.tsx).
 * Strapi multi-upload order: 1st image → 59, 2nd → 57, 3rd → 60, 4th → 56, 5th → 53.
 */
export const ABOUT_COLLAGE_SLOT_ORDER: AboutCollageSlot[] = ['59', '57', '60', '56', '53']

/** Match uploads to Figma rectangles by filename / URL stem */
export const ABOUT_COLLAGE_SLOT_STEMS: Record<AboutCollageSlot, readonly string[]> = {
  '59': ['2149060246'],
  '57': ['bf404d08198a86ab83b0df723bc84087', '84087'],
  '60': ['3735'],
  '56': ['2149006892'],
  '53': ['2148040518', '6eab8aeb'],
}

export interface CmsMediaItem {
  url: string
  name: string
  updatedAt: string
}

export type AboutCollageBySlot = Partial<Record<AboutCollageSlot, string>>

/** Unwrap Strapi v4/v5 media list shapes into a flat array of entries */
function normalizeImageList(imageField: unknown): unknown[] {
  if (!imageField) return []
  if (Array.isArray(imageField)) return imageField

  if (typeof imageField === 'object' && imageField !== null) {
    const data = (imageField as Record<string, unknown>).data
    if (Array.isArray(data)) return data
    return [imageField]
  }

  return []
}

/** Parse Strapi `Image` multi-upload field into normalized media items (CMS list order preserved) */
export function extractAboutMediaItems(imageField: unknown): CmsMediaItem[] {
  return normalizeImageList(imageField)
    .map((entry) => {
      const fields = getStrapiFields(entry)
      const raw = entry && typeof entry === 'object' ? (entry as Record<string, unknown>) : {}
      const url = extractMediaUrl(entry)
      if (!url) return null

      const name =
        typeof raw.name === 'string'
          ? raw.name
          : typeof fields.name === 'string'
            ? fields.name
            : ''

      const updatedAt =
        typeof raw.updatedAt === 'string'
          ? raw.updatedAt
          : typeof fields.updatedAt === 'string'
            ? fields.updatedAt
            : typeof raw.createdAt === 'string'
              ? raw.createdAt
              : typeof fields.createdAt === 'string'
                ? fields.createdAt
                : ''

      return { url, name, updatedAt }
    })
    .filter((item): item is CmsMediaItem => item !== null)
}

function mediaMatchesStem(item: CmsMediaItem, stem: string): boolean {
  const haystack = `${item.name} ${item.url}`.toLowerCase()
  return haystack.includes(stem.toLowerCase())
}

/**
 * Map CMS images to collage slots by filename stem (preferred), then upload index.
 */
export function buildAboutCollageBySlot(items: CmsMediaItem[]): AboutCollageBySlot {
  const result: AboutCollageBySlot = {}
  const usedIndices = new Set<number>()

  for (const slot of ABOUT_COLLAGE_SLOT_ORDER) {
    const stems = ABOUT_COLLAGE_SLOT_STEMS[slot]
    let bestIndex = -1
    let bestUpdatedAt = ''

    items.forEach((item, index) => {
      if (usedIndices.has(index)) return
      if (!stems.some((stem) => mediaMatchesStem(item, stem))) return
      if (bestIndex < 0 || item.updatedAt > bestUpdatedAt) {
        bestIndex = index
        bestUpdatedAt = item.updatedAt
      }
    })

    if (bestIndex >= 0) {
      usedIndices.add(bestIndex)
      result[slot] = items[bestIndex]!.url
    }
  }

  ABOUT_COLLAGE_SLOT_ORDER.forEach((slot, index) => {
    if (result[slot]) return
    if (usedIndices.has(index)) return
    const item = items[index]
    if (!item?.url) return
    usedIndices.add(index)
    result[slot] = item.url
  })

  return result
}

export function parseAboutCollageFromCms(imageField: unknown): {
  items: CmsMediaItem[]
  bySlot: AboutCollageBySlot
  urls: string[]
} {
  const items = extractAboutMediaItems(imageField)
  const bySlot = buildAboutCollageBySlot(items)
  const urls = items.map((item) => item.url)
  return { items, bySlot, urls }
}
